// Genre and Theme constants with Book Type organization
// Book Type is the primary classification: Fiction or Non-Fiction
// Genres are subcategories within each Book Type
// Themes are associated with specific Genres
// 
// Updated: January 2026 - Expanded for more nuanced categorization

// Book Type options
export const BOOK_TYPE_OPTIONS = [
  { value: 'fiction', label: 'Fiction' },
  { value: 'non-fiction', label: 'Non-Fiction' },
  { value: 'textbook', label: 'Text Book' },
  { value: 'instruction-manual', label: 'Instruction Manual' },
  { value: 'published-paper', label: 'Published Paper' },
  { value: 'children', label: "Children's Book" }
];

// ============================================================================
// FICTION GENRES (Expanded & Alphabetized)
// ============================================================================
export const FICTION_GENRES = [
  { value: '', label: 'Select genre' },
  { value: 'action-adventure', label: 'Action & Adventure' },
  { value: 'alternate-history', label: 'Alternate History' },
  { value: 'anthology', label: 'Anthology & Collections' },
  { value: 'children', label: "Children's Fiction" },
  { value: 'classics', label: 'Classics' },
  { value: 'cli-fi', label: 'Climate Fiction (Cli-Fi)' },
  { value: 'contemporary', label: 'Contemporary Fiction' },
  { value: 'crime', label: 'Crime & Detective' },
  { value: 'cyberpunk', label: 'Cyberpunk' },
  { value: 'dystopian', label: 'Dystopian & Post-Apocalyptic' },
  { value: 'espionage', label: 'Espionage & Spy' },
  { value: 'fairy-tale', label: 'Fairy Tale & Folklore' },
  { value: 'family-saga', label: 'Family Saga' },
  { value: 'fantasy', label: 'Fantasy' },
  { value: 'flash-fiction', label: 'Flash Fiction & Micro Fiction' },
  { value: 'gothic', label: 'Gothic' },
  { value: 'historical-fiction', label: 'Historical Fiction' },
  { value: 'horror', label: 'Horror' },
  { value: 'humor', label: 'Humor & Satire' },
  { value: 'legal-thriller', label: 'Legal Thriller' },
  { value: 'literary-fiction', label: 'Literary Fiction' },
  { value: 'magical-realism', label: 'Magical Realism' },
  { value: 'medical-thriller', label: 'Medical Thriller' },
  { value: 'military-fiction', label: 'Military Fiction' },
  { value: 'mystery', label: 'Mystery' },
  { value: 'mythology', label: 'Mythology & Legends' },
  { value: 'noir', label: 'Noir' },
  { value: 'paranormal', label: 'Paranormal & Supernatural' },
  { value: 'philosophical-fiction', label: 'Philosophical Fiction' },
  { value: 'political-fiction', label: 'Political Fiction' },
  { value: 'psychological-fiction', label: 'Psychological Fiction' },
  { value: 'romance', label: 'Romance' },
  { value: 'science-fiction', label: 'Science Fiction' },
  { value: 'short-stories', label: 'Short Stories' },
  { value: 'southern-fiction', label: 'Southern Fiction' },
  { value: 'space-opera', label: 'Space Opera' },
  { value: 'speculative', label: 'Speculative Fiction' },
  { value: 'steampunk', label: 'Steampunk' },
  { value: 'suspense', label: 'Suspense' },
  { value: 'techno-thriller', label: 'Techno-Thriller' },
  { value: 'thriller', label: 'Thriller' },
  { value: 'urban-fantasy', label: 'Urban Fantasy' },
  { value: 'war-fiction', label: 'War Fiction' },
  { value: 'western', label: 'Western' },
  { value: 'womens-fiction', label: "Women's Fiction" },
  { value: 'young-adult', label: 'Young Adult (YA)' },
  { value: 'other-fiction', label: 'Other Fiction' }
];

// ============================================================================
// NON-FICTION GENRES (Significantly Expanded)
// ============================================================================
export const NON_FICTION_GENRES = [
  { value: '', label: 'Select genre' },
  
  // Biography & Memoir
  { value: 'autobiography', label: 'Autobiography' },
  { value: 'biography', label: 'Biography' },
  { value: 'memoir', label: 'Memoir' },
  
  // Business & Professional (Expanded)
  { value: 'business-entrepreneurship', label: 'Business - Entrepreneurship & Startups' },
  { value: 'business-finance', label: 'Business - Finance & Investing' },
  { value: 'business-leadership', label: 'Business - Leadership & Management' },
  { value: 'business-marketing', label: 'Business - Marketing & Sales' },
  { value: 'business-operations', label: 'Business - Operations & Strategy' },
  { value: 'business-real-estate', label: 'Business - Real Estate' },
  { value: 'business-small-business', label: 'Business - Small Business & Hospitality' },
  { value: 'career-development', label: 'Career Development & Professional Growth' },
  { value: 'economics', label: 'Economics' },
  
  // Computers & Technology (New Category)
  { value: 'tech-ai-ml', label: 'Technology - AI & Machine Learning' },
  { value: 'tech-cybersecurity', label: 'Technology - Cybersecurity & Privacy' },
  { value: 'tech-digital-transformation', label: 'Technology - Digital Transformation' },
  { value: 'tech-future-trends', label: 'Technology - Future Trends & Predictions' },
  { value: 'tech-general', label: 'Technology - General' },
  { value: 'tech-social-impact', label: 'Technology - Social & Cultural Impact' },
  
  // Education & Reference
  { value: 'education', label: 'Education & Teaching' },
  { value: 'reference', label: 'Reference & Study Guides' },
  
  // Health & Wellness (Expanded)
  { value: 'health-diet-nutrition', label: 'Health - Diet & Nutrition' },
  { value: 'health-fitness', label: 'Health - Fitness & Exercise' },
  { value: 'health-medical', label: 'Health - Medical & Disease' },
  { value: 'health-mental', label: 'Health - Mental Health & Psychology' },
  { value: 'health-wellness', label: 'Health - Wellness & Longevity' },
  { value: 'health-alternative', label: 'Health - Alternative & Holistic' },
  
  // History & Politics
  { value: 'history-american', label: 'History - American' },
  { value: 'history-world', label: 'History - World' },
  { value: 'history-military', label: 'History - Military' },
  { value: 'political-science', label: 'Political Science & Current Affairs' },
  { value: 'social-commentary', label: 'Social Commentary & Cultural Criticism' },
  
  // Lifestyle
  { value: 'cookbook', label: 'Cookbook & Food Writing' },
  { value: 'crafts-hobbies', label: 'Crafts & Hobbies' },
  { value: 'home-garden', label: 'Home & Garden' },
  { value: 'travel', label: 'Travel & Adventure' },
  { value: 'sports-recreation', label: 'Sports & Recreation' },
  { value: 'pets-animals', label: 'Pets & Animal Care' },
  
  // Mind & Spirit
  { value: 'philosophy', label: 'Philosophy' },
  { value: 'psychology', label: 'Psychology & Behavioral Science' },
  { value: 'religion-spirituality', label: 'Religion & Spirituality' },
  { value: 'mindfulness', label: 'Mindfulness & Meditation' },
  
  // Personal Development (Expanded)
  { value: 'self-help-general', label: 'Self-Help - General' },
  { value: 'self-help-habits', label: 'Self-Help - Habits & Productivity' },
  { value: 'self-help-relationships', label: 'Self-Help - Relationships & Communication' },
  { value: 'self-help-motivation', label: 'Self-Help - Motivation & Success' },
  { value: 'self-help-confidence', label: 'Self-Help - Confidence & Self-Esteem' },
  { value: 'self-help-creativity', label: 'Self-Help - Creativity & Personal Expression' },
  { value: 'personal-finance', label: 'Personal Finance & Money Management' },
  { value: 'parenting-family', label: 'Parenting & Family' },
  
  // Science & Nature
  { value: 'science-general', label: 'Science - General & Popular Science' },
  { value: 'science-biology', label: 'Science - Biology & Life Sciences' },
  { value: 'science-physics', label: 'Science - Physics & Astronomy' },
  { value: 'science-environment', label: 'Science - Environment & Climate' },
  { value: 'nature-outdoors', label: 'Nature & Outdoors' },
  
  // Arts & Entertainment
  { value: 'art-photography', label: 'Art & Photography' },
  { value: 'music', label: 'Music' },
  { value: 'film-media', label: 'Film & Media Studies' },
  { value: 'performing-arts', label: 'Performing Arts' },
  
  // Writing & Language
  { value: 'writing-publishing', label: 'Writing & Publishing' },
  { value: 'essays', label: 'Essays & Creative Non-Fiction' },
  { value: 'journalism', label: 'Journalism & Reporting' },
  { value: 'poetry-nonfiction', label: 'Poetry' },
  { value: 'true-crime', label: 'True Crime' },
  
  { value: 'other-nonfiction', label: 'Other Non-Fiction' }
];

// ============================================================================
// CHILDREN'S GENRES
// ============================================================================
export const CHILDREN_GENRES = [
  { value: '', label: 'Select genre' },
  { value: 'picture-book', label: 'Picture Book' },
  { value: 'early-reader', label: 'Early Reader' },
  { value: 'middle-grade', label: 'Middle Grade' },
  { value: 'young-adult', label: 'Young Adult' },
  { value: 'childrens-educational', label: "Children's Educational" },
  { value: 'childrens-activity', label: "Children's Activity & Interactive" },
  { value: 'childrens-poetry', label: "Children's Poetry & Rhymes" },
  { value: 'childrens-adventure', label: "Children's Adventure" },
  { value: 'childrens-fantasy', label: "Children's Fantasy & Magic" },
  { value: 'childrens-animals', label: "Children's Animals & Nature" },
  { value: 'childrens-humor', label: "Children's Humor" },
  { value: 'childrens-holiday', label: 'Holiday & Seasonal' },
  { value: 'other-children', label: 'Other' }
];

// Combined Genre Options (for backwards compatibility and "all" views)
export const GENRE_OPTIONS = [
  { value: '', label: 'Select genre' },
  ...FICTION_GENRES.filter(g => g.value !== ''),
  ...NON_FICTION_GENRES.filter(g => g.value !== '' && !FICTION_GENRES.some(f => f.value === g.value)),
  ...CHILDREN_GENRES.filter(g => g.value !== '' && !FICTION_GENRES.some(f => f.value === g.value) && !NON_FICTION_GENRES.some(n => n.value === g.value))
].sort((a, b) => {
  if (a.value === '') return -1;
  if (b.value === '') return 1;
  return a.label.localeCompare(b.label);
});

// Helper function to get genres for a given book type
export const getGenresForBookType = (bookType) => {
  if (bookType === 'fiction') return FICTION_GENRES;
  if (bookType === 'non-fiction') return NON_FICTION_GENRES;
  if (bookType === 'children') return CHILDREN_GENRES;
  return GENRE_OPTIONS; // Return all if no type specified
};

// ============================================================================
// THEME MAPPINGS (Expanded with genre-specific relevance)
// ============================================================================

// Master list of all themes (alphabetically ordered)
const ALL_AVAILABLE_THEMES = [
  { value: '', label: 'Select theme' },
  // Achievement & Success
  { value: 'achievement', label: 'Achievement' },
  { value: 'ambition', label: 'Ambition' },
  { value: 'competition', label: 'Competition' },
  { value: 'excellence', label: 'Excellence & Mastery' },
  { value: 'success', label: 'Success' },
  { value: 'wealth', label: 'Wealth & Prosperity' },
  
  // Change & Growth
  { value: 'adaptation', label: 'Adaptation' },
  { value: 'change', label: 'Change & Transition' },
  { value: 'coming-of-age', label: 'Coming of Age' },
  { value: 'evolution', label: 'Evolution' },
  { value: 'growth', label: 'Personal Growth' },
  { value: 'reinvention', label: 'Reinvention' },
  { value: 'transformation', label: 'Transformation' },
  
  // Conflict & Struggle
  { value: 'adversity', label: 'Adversity & Hardship' },
  { value: 'betrayal', label: 'Betrayal' },
  { value: 'conflict', label: 'Conflict' },
  { value: 'disruption', label: 'Disruption' },
  { value: 'good-vs-evil', label: 'Good vs. Evil' },
  { value: 'rebellion', label: 'Rebellion & Resistance' },
  { value: 'revenge', label: 'Revenge' },
  { value: 'survival', label: 'Survival' },
  { value: 'war', label: 'War & Conflict' },
  
  // Discovery & Knowledge
  { value: 'creativity', label: 'Creativity' },
  { value: 'curiosity', label: 'Curiosity' },
  { value: 'discovery', label: 'Discovery' },
  { value: 'education', label: 'Education & Learning' },
  { value: 'enlightenment', label: 'Enlightenment' },
  { value: 'exploration', label: 'Exploration' },
  { value: 'innovation', label: 'Innovation' },
  { value: 'knowledge', label: 'Knowledge & Wisdom' },
  { value: 'truth', label: 'Truth & Authenticity' },
  
  // Emotions & Psychology
  { value: 'anxiety', label: 'Anxiety & Stress' },
  { value: 'courage', label: 'Courage' },
  { value: 'empathy', label: 'Empathy' },
  { value: 'fear', label: 'Fear' },
  { value: 'grief', label: 'Grief & Loss' },
  { value: 'happiness', label: 'Happiness & Joy' },
  { value: 'hope', label: 'Hope' },
  { value: 'imagination', label: 'Imagination' },
  { value: 'kindness', label: 'Kindness' },
  { value: 'loneliness', label: 'Loneliness & Isolation' },
  { value: 'nostalgia', label: 'Nostalgia' },
  { value: 'passion', label: 'Passion' },
  { value: 'resilience', label: 'Resilience' },
  
  // Ethics & Values
  { value: 'ethics', label: 'Ethics' },
  { value: 'honor', label: 'Honor & Integrity' },
  { value: 'justice', label: 'Justice' },
  { value: 'morality', label: 'Morality' },
  { value: 'responsibility', label: 'Responsibility' },
  { value: 'sacrifice', label: 'Sacrifice' },
  { value: 'virtue', label: 'Virtue' },
  
  // Identity & Self
  { value: 'authenticity', label: 'Authenticity' },
  { value: 'belonging', label: 'Belonging' },
  { value: 'identity', label: 'Identity' },
  { value: 'individuality', label: 'Individuality' },
  { value: 'purpose', label: 'Purpose & Meaning' },
  { value: 'self-discovery', label: 'Self-Discovery' },
  { value: 'self-acceptance', label: 'Self-Acceptance' },
  
  // Leadership & Influence
  { value: 'authority', label: 'Authority' },
  { value: 'empowerment', label: 'Empowerment' },
  { value: 'influence', label: 'Influence' },
  { value: 'leadership', label: 'Leadership' },
  { value: 'mentorship', label: 'Mentorship' },
  { value: 'power', label: 'Power' },
  { value: 'service', label: 'Service & Giving Back' },
  
  // Life & Death
  { value: 'aging', label: 'Aging & Mortality' },
  { value: 'death', label: 'Death & Mortality' },
  { value: 'legacy', label: 'Legacy' },
  { value: 'life-lessons', label: 'Life Lessons' },
  { value: 'mortality', label: 'Mortality' },
  { value: 'rebirth', label: 'Rebirth & Renewal' },
  
  // Relationships
  { value: 'community', label: 'Community' },
  { value: 'connection', label: 'Connection' },
  { value: 'family', label: 'Family' },
  { value: 'friendship', label: 'Friendship' },
  { value: 'love', label: 'Love' },
  { value: 'loyalty', label: 'Loyalty' },
  { value: 'romance', label: 'Romance' },
  { value: 'trust', label: 'Trust' },
  
  // Society & Culture
  { value: 'class', label: 'Class & Social Mobility' },
  { value: 'conformity', label: 'Conformity vs. Nonconformity' },
  { value: 'consumerism', label: 'Consumerism' },
  { value: 'corruption', label: 'Corruption' },
  { value: 'culture', label: 'Culture & Heritage' },
  { value: 'diversity', label: 'Diversity & Inclusion' },
  { value: 'equality', label: 'Equality' },
  { value: 'freedom', label: 'Freedom' },
  { value: 'globalization', label: 'Globalization' },
  { value: 'inequality', label: 'Inequality' },
  { value: 'privacy', label: 'Privacy & Surveillance' },
  { value: 'social-issues', label: 'Social Issues' },
  { value: 'tradition', label: 'Tradition vs. Progress' },
  
  // Spirituality & Philosophy
  { value: 'destiny', label: 'Destiny & Fate' },
  { value: 'faith', label: 'Faith' },
  { value: 'mindfulness', label: 'Mindfulness' },
  { value: 'redemption', label: 'Redemption' },
  { value: 'spirituality', label: 'Spirituality' },
  { value: 'transcendence', label: 'Transcendence' },
  
  // Technology & Progress
  { value: 'automation', label: 'Automation & AI' },
  { value: 'ai-ethics', label: 'AI Ethics' },
  { value: 'digital-life', label: 'Digital Life' },
  { value: 'future-of-work', label: 'Future of Work' },
  { value: 'human-machine', label: 'Human-Machine Relationship' },
  { value: 'human-potential', label: 'Human Potential' },
  { value: 'progress', label: 'Progress' },
  { value: 'singularity', label: 'Singularity' },
  { value: 'technology', label: 'Technology' },
  { value: 'transhumanism', label: 'Transhumanism' },
  
  // World & Nature
  { value: 'adventure', label: 'Adventure' },
  { value: 'environment', label: 'Environment & Sustainability' },
  { value: 'magic', label: 'Magic' },
  { value: 'nature', label: 'Nature' },
  { value: 'place', label: 'Sense of Place' },
  { value: 'wilderness', label: 'Wilderness' },
  
  // Work & Career
  { value: 'entrepreneurship', label: 'Entrepreneurship' },
  { value: 'hustle', label: 'Hustle & Work Ethic' },
  { value: 'innovation-business', label: 'Business Innovation' },
  { value: 'perseverance', label: 'Perseverance' },
  { value: 'productivity', label: 'Productivity' },
  { value: 'work-life-balance', label: 'Work-Life Balance' },
  
  { value: 'other', label: 'Other' }
];

// Genre to Themes mapping - curated themes appropriate for each genre
export const GENRE_THEMES = {
  // Default: All themes available
  '': ALL_AVAILABLE_THEMES,
  
  // ========================================================================
  // FICTION GENRES - Theme Mappings
  // ========================================================================
  
  'action-adventure': [
    { value: '', label: 'Select theme' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'courage', label: 'Courage' },
    { value: 'discovery', label: 'Discovery' },
    { value: 'exploration', label: 'Exploration' },
    { value: 'freedom', label: 'Freedom' },
    { value: 'good-vs-evil', label: 'Good vs. Evil' },
    { value: 'honor', label: 'Honor & Integrity' },
    { value: 'justice', label: 'Justice' },
    { value: 'loyalty', label: 'Loyalty' },
    { value: 'perseverance', label: 'Perseverance' },
    { value: 'survival', label: 'Survival' },
    { value: 'war', label: 'War & Conflict' },
    { value: 'other', label: 'Other' }
  ],
  
  'alternate-history': [
    { value: '', label: 'Select theme' },
    { value: 'destiny', label: 'Destiny & Fate' },
    { value: 'freedom', label: 'Freedom' },
    { value: 'identity', label: 'Identity' },
    { value: 'morality', label: 'Morality' },
    { value: 'power', label: 'Power' },
    { value: 'progress', label: 'Progress' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'tradition', label: 'Tradition vs. Progress' },
    { value: 'war', label: 'War & Conflict' },
    { value: 'other', label: 'Other' }
  ],
  
  'anthology': ALL_AVAILABLE_THEMES,
  
  'children': [
    { value: '', label: 'Select theme' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'belonging', label: 'Belonging' },
    { value: 'coming-of-age', label: 'Coming of Age' },
    { value: 'courage', label: 'Courage' },
    { value: 'curiosity', label: 'Curiosity' },
    { value: 'empathy', label: 'Empathy' },
    { value: 'family', label: 'Family' },
    { value: 'friendship', label: 'Friendship' },
    { value: 'good-vs-evil', label: 'Good vs. Evil' },
    { value: 'identity', label: 'Identity' },
    { value: 'imagination', label: 'Imagination' },
    { value: 'kindness', label: 'Kindness' },
    { value: 'other', label: 'Other' }
  ],
  
  'classics': ALL_AVAILABLE_THEMES,
  
  'cli-fi': [
    { value: '', label: 'Select theme' },
    { value: 'adaptation', label: 'Adaptation' },
    { value: 'environment', label: 'Environment & Sustainability' },
    { value: 'hope', label: 'Hope' },
    { value: 'morality', label: 'Morality' },
    { value: 'nature', label: 'Nature' },
    { value: 'responsibility', label: 'Responsibility' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'survival', label: 'Survival' },
    { value: 'technology', label: 'Technology' },
    { value: 'other', label: 'Other' }
  ],
  
  'contemporary': [
    { value: '', label: 'Select theme' },
    { value: 'authenticity', label: 'Authenticity' },
    { value: 'belonging', label: 'Belonging' },
    { value: 'coming-of-age', label: 'Coming of Age' },
    { value: 'connection', label: 'Connection' },
    { value: 'digital-life', label: 'Digital Life' },
    { value: 'family', label: 'Family' },
    { value: 'friendship', label: 'Friendship' },
    { value: 'identity', label: 'Identity' },
    { value: 'love', label: 'Love' },
    { value: 'purpose', label: 'Purpose & Meaning' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'work-life-balance', label: 'Work-Life Balance' },
    { value: 'other', label: 'Other' }
  ],
  
  'crime': [
    { value: '', label: 'Select theme' },
    { value: 'betrayal', label: 'Betrayal' },
    { value: 'corruption', label: 'Corruption' },
    { value: 'ethics', label: 'Ethics' },
    { value: 'justice', label: 'Justice' },
    { value: 'morality', label: 'Morality' },
    { value: 'power', label: 'Power' },
    { value: 'redemption', label: 'Redemption' },
    { value: 'revenge', label: 'Revenge' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'other', label: 'Other' }
  ],
  
  'cyberpunk': [
    { value: '', label: 'Select theme' },
    { value: 'automation', label: 'Automation & AI' },
    { value: 'class', label: 'Class & Social Mobility' },
    { value: 'corruption', label: 'Corruption' },
    { value: 'digital-life', label: 'Digital Life' },
    { value: 'freedom', label: 'Freedom' },
    { value: 'human-machine', label: 'Human-Machine Relationship' },
    { value: 'identity', label: 'Identity' },
    { value: 'inequality', label: 'Inequality' },
    { value: 'privacy', label: 'Privacy & Surveillance' },
    { value: 'rebellion', label: 'Rebellion & Resistance' },
    { value: 'technology', label: 'Technology' },
    { value: 'transhumanism', label: 'Transhumanism' },
    { value: 'other', label: 'Other' }
  ],
  
  'dystopian': [
    { value: '', label: 'Select theme' },
    { value: 'conformity', label: 'Conformity vs. Nonconformity' },
    { value: 'corruption', label: 'Corruption' },
    { value: 'freedom', label: 'Freedom' },
    { value: 'identity', label: 'Identity' },
    { value: 'inequality', label: 'Inequality' },
    { value: 'morality', label: 'Morality' },
    { value: 'power', label: 'Power' },
    { value: 'privacy', label: 'Privacy & Surveillance' },
    { value: 'rebellion', label: 'Rebellion & Resistance' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'survival', label: 'Survival' },
    { value: 'technology', label: 'Technology' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'other', label: 'Other' }
  ],
  
  'espionage': [
    { value: '', label: 'Select theme' },
    { value: 'betrayal', label: 'Betrayal' },
    { value: 'courage', label: 'Courage' },
    { value: 'identity', label: 'Identity' },
    { value: 'loyalty', label: 'Loyalty' },
    { value: 'morality', label: 'Morality' },
    { value: 'power', label: 'Power' },
    { value: 'sacrifice', label: 'Sacrifice' },
    { value: 'trust', label: 'Trust' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'war', label: 'War & Conflict' },
    { value: 'other', label: 'Other' }
  ],
  
  'fairy-tale': [
    { value: '', label: 'Select theme' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'courage', label: 'Courage' },
    { value: 'destiny', label: 'Destiny & Fate' },
    { value: 'good-vs-evil', label: 'Good vs. Evil' },
    { value: 'love', label: 'Love' },
    { value: 'morality', label: 'Morality' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'virtue', label: 'Virtue' },
    { value: 'other', label: 'Other' }
  ],
  
  'family-saga': [
    { value: '', label: 'Select theme' },
    { value: 'betrayal', label: 'Betrayal' },
    { value: 'belonging', label: 'Belonging' },
    { value: 'culture', label: 'Culture & Heritage' },
    { value: 'family', label: 'Family' },
    { value: 'identity', label: 'Identity' },
    { value: 'legacy', label: 'Legacy' },
    { value: 'love', label: 'Love' },
    { value: 'loyalty', label: 'Loyalty' },
    { value: 'sacrifice', label: 'Sacrifice' },
    { value: 'tradition', label: 'Tradition vs. Progress' },
    { value: 'other', label: 'Other' }
  ],
  
  'fantasy': [
    { value: '', label: 'Select theme' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'coming-of-age', label: 'Coming of Age' },
    { value: 'courage', label: 'Courage' },
    { value: 'destiny', label: 'Destiny & Fate' },
    { value: 'good-vs-evil', label: 'Good vs. Evil' },
    { value: 'honor', label: 'Honor & Integrity' },
    { value: 'identity', label: 'Identity' },
    { value: 'power', label: 'Power' },
    { value: 'redemption', label: 'Redemption' },
    { value: 'sacrifice', label: 'Sacrifice' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'flash-fiction': ALL_AVAILABLE_THEMES,
  
  'gothic': [
    { value: '', label: 'Select theme' },
    { value: 'death', label: 'Death & Mortality' },
    { value: 'fear', label: 'Fear' },
    { value: 'grief', label: 'Grief & Loss' },
    { value: 'identity', label: 'Identity' },
    { value: 'isolation', label: 'Loneliness & Isolation' },
    { value: 'morality', label: 'Morality' },
    { value: 'mystery', label: 'Mystery' },
    { value: 'redemption', label: 'Redemption' },
    { value: 'romance', label: 'Romance' },
    { value: 'other', label: 'Other' }
  ],
  
  'historical-fiction': [
    { value: '', label: 'Select theme' },
    { value: 'class', label: 'Class & Social Mobility' },
    { value: 'courage', label: 'Courage' },
    { value: 'culture', label: 'Culture & Heritage' },
    { value: 'family', label: 'Family' },
    { value: 'freedom', label: 'Freedom' },
    { value: 'honor', label: 'Honor & Integrity' },
    { value: 'identity', label: 'Identity' },
    { value: 'love', label: 'Love' },
    { value: 'sacrifice', label: 'Sacrifice' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'survival', label: 'Survival' },
    { value: 'tradition', label: 'Tradition vs. Progress' },
    { value: 'war', label: 'War & Conflict' },
    { value: 'other', label: 'Other' }
  ],
  
  'horror': [
    { value: '', label: 'Select theme' },
    { value: 'death', label: 'Death & Mortality' },
    { value: 'fear', label: 'Fear' },
    { value: 'good-vs-evil', label: 'Good vs. Evil' },
    { value: 'grief', label: 'Grief & Loss' },
    { value: 'identity', label: 'Identity' },
    { value: 'isolation', label: 'Loneliness & Isolation' },
    { value: 'morality', label: 'Morality' },
    { value: 'revenge', label: 'Revenge' },
    { value: 'survival', label: 'Survival' },
    { value: 'other', label: 'Other' }
  ],
  
  'humor': [
    { value: '', label: 'Select theme' },
    { value: 'absurdity', label: 'Absurdity' },
    { value: 'belonging', label: 'Belonging' },
    { value: 'family', label: 'Family' },
    { value: 'friendship', label: 'Friendship' },
    { value: 'identity', label: 'Identity' },
    { value: 'love', label: 'Love' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'legal-thriller': [
    { value: '', label: 'Select theme' },
    { value: 'betrayal', label: 'Betrayal' },
    { value: 'corruption', label: 'Corruption' },
    { value: 'ethics', label: 'Ethics' },
    { value: 'justice', label: 'Justice' },
    { value: 'morality', label: 'Morality' },
    { value: 'power', label: 'Power' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'other', label: 'Other' }
  ],
  
  'literary-fiction': [
    { value: '', label: 'Select theme' },
    { value: 'authenticity', label: 'Authenticity' },
    { value: 'coming-of-age', label: 'Coming of Age' },
    { value: 'death', label: 'Death & Mortality' },
    { value: 'family', label: 'Family' },
    { value: 'grief', label: 'Grief & Loss' },
    { value: 'identity', label: 'Identity' },
    { value: 'love', label: 'Love' },
    { value: 'morality', label: 'Morality' },
    { value: 'purpose', label: 'Purpose & Meaning' },
    { value: 'redemption', label: 'Redemption' },
    { value: 'self-discovery', label: 'Self-Discovery' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'magical-realism': [
    { value: '', label: 'Select theme' },
    { value: 'culture', label: 'Culture & Heritage' },
    { value: 'destiny', label: 'Destiny & Fate' },
    { value: 'family', label: 'Family' },
    { value: 'identity', label: 'Identity' },
    { value: 'love', label: 'Love' },
    { value: 'spirituality', label: 'Spirituality' },
    { value: 'tradition', label: 'Tradition vs. Progress' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'medical-thriller': [
    { value: '', label: 'Select theme' },
    { value: 'corruption', label: 'Corruption' },
    { value: 'ethics', label: 'Ethics' },
    { value: 'fear', label: 'Fear' },
    { value: 'morality', label: 'Morality' },
    { value: 'power', label: 'Power' },
    { value: 'survival', label: 'Survival' },
    { value: 'technology', label: 'Technology' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'other', label: 'Other' }
  ],
  
  'military-fiction': [
    { value: '', label: 'Select theme' },
    { value: 'brotherhood', label: 'Brotherhood' },
    { value: 'courage', label: 'Courage' },
    { value: 'honor', label: 'Honor & Integrity' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'loyalty', label: 'Loyalty' },
    { value: 'morality', label: 'Morality' },
    { value: 'sacrifice', label: 'Sacrifice' },
    { value: 'survival', label: 'Survival' },
    { value: 'war', label: 'War & Conflict' },
    { value: 'other', label: 'Other' }
  ],
  
  'mystery': [
    { value: '', label: 'Select theme' },
    { value: 'betrayal', label: 'Betrayal' },
    { value: 'justice', label: 'Justice' },
    { value: 'morality', label: 'Morality' },
    { value: 'obsession', label: 'Obsession' },
    { value: 'power', label: 'Power' },
    { value: 'redemption', label: 'Redemption' },
    { value: 'revenge', label: 'Revenge' },
    { value: 'secrets', label: 'Secrets' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'other', label: 'Other' }
  ],
  
  'mythology': [
    { value: '', label: 'Select theme' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'courage', label: 'Courage' },
    { value: 'destiny', label: 'Destiny & Fate' },
    { value: 'good-vs-evil', label: 'Good vs. Evil' },
    { value: 'honor', label: 'Honor & Integrity' },
    { value: 'love', label: 'Love' },
    { value: 'sacrifice', label: 'Sacrifice' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'noir': [
    { value: '', label: 'Select theme' },
    { value: 'betrayal', label: 'Betrayal' },
    { value: 'corruption', label: 'Corruption' },
    { value: 'fate', label: 'Destiny & Fate' },
    { value: 'isolation', label: 'Loneliness & Isolation' },
    { value: 'morality', label: 'Morality' },
    { value: 'obsession', label: 'Obsession' },
    { value: 'redemption', label: 'Redemption' },
    { value: 'other', label: 'Other' }
  ],
  
  'paranormal': [
    { value: '', label: 'Select theme' },
    { value: 'death', label: 'Death & Mortality' },
    { value: 'destiny', label: 'Destiny & Fate' },
    { value: 'fear', label: 'Fear' },
    { value: 'good-vs-evil', label: 'Good vs. Evil' },
    { value: 'identity', label: 'Identity' },
    { value: 'love', label: 'Love' },
    { value: 'redemption', label: 'Redemption' },
    { value: 'spirituality', label: 'Spirituality' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'philosophical-fiction': [
    { value: '', label: 'Select theme' },
    { value: 'death', label: 'Death & Mortality' },
    { value: 'ethics', label: 'Ethics' },
    { value: 'existence', label: 'Existence' },
    { value: 'freedom', label: 'Freedom' },
    { value: 'identity', label: 'Identity' },
    { value: 'knowledge', label: 'Knowledge & Wisdom' },
    { value: 'morality', label: 'Morality' },
    { value: 'purpose', label: 'Purpose & Meaning' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'other', label: 'Other' }
  ],
  
  'political-fiction': [
    { value: '', label: 'Select theme' },
    { value: 'corruption', label: 'Corruption' },
    { value: 'freedom', label: 'Freedom' },
    { value: 'justice', label: 'Justice' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'morality', label: 'Morality' },
    { value: 'power', label: 'Power' },
    { value: 'rebellion', label: 'Rebellion & Resistance' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'other', label: 'Other' }
  ],
  
  'psychological-fiction': [
    { value: '', label: 'Select theme' },
    { value: 'anxiety', label: 'Anxiety & Stress' },
    { value: 'fear', label: 'Fear' },
    { value: 'grief', label: 'Grief & Loss' },
    { value: 'identity', label: 'Identity' },
    { value: 'isolation', label: 'Loneliness & Isolation' },
    { value: 'morality', label: 'Morality' },
    { value: 'obsession', label: 'Obsession' },
    { value: 'self-discovery', label: 'Self-Discovery' },
    { value: 'trauma', label: 'Trauma' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'other', label: 'Other' }
  ],
  
  'romance': [
    { value: '', label: 'Select theme' },
    { value: 'betrayal', label: 'Betrayal' },
    { value: 'coming-of-age', label: 'Coming of Age' },
    { value: 'connection', label: 'Connection' },
    { value: 'destiny', label: 'Destiny & Fate' },
    { value: 'family', label: 'Family' },
    { value: 'friendship', label: 'Friendship' },
    { value: 'hope', label: 'Hope' },
    { value: 'identity', label: 'Identity' },
    { value: 'love', label: 'Love' },
    { value: 'passion', label: 'Passion' },
    { value: 'redemption', label: 'Redemption' },
    { value: 'sacrifice', label: 'Sacrifice' },
    { value: 'self-discovery', label: 'Self-Discovery' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'trust', label: 'Trust' },
    { value: 'other', label: 'Other' }
  ],
  
  'science-fiction': [
    { value: '', label: 'Select theme' },
    { value: 'automation', label: 'Automation & AI' },
    { value: 'discovery', label: 'Discovery' },
    { value: 'ethics', label: 'Ethics' },
    { value: 'exploration', label: 'Exploration' },
    { value: 'freedom', label: 'Freedom' },
    { value: 'human-machine', label: 'Human-Machine Relationship' },
    { value: 'identity', label: 'Identity' },
    { value: 'innovation', label: 'Innovation' },
    { value: 'morality', label: 'Morality' },
    { value: 'power', label: 'Power' },
    { value: 'progress', label: 'Progress' },
    { value: 'survival', label: 'Survival' },
    { value: 'technology', label: 'Technology' },
    { value: 'transhumanism', label: 'Transhumanism' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'war', label: 'War & Conflict' },
    { value: 'other', label: 'Other' }
  ],
  
  'short-stories': ALL_AVAILABLE_THEMES,
  
  'southern-fiction': [
    { value: '', label: 'Select theme' },
    { value: 'class', label: 'Class & Social Mobility' },
    { value: 'culture', label: 'Culture & Heritage' },
    { value: 'family', label: 'Family' },
    { value: 'identity', label: 'Identity' },
    { value: 'legacy', label: 'Legacy' },
    { value: 'place', label: 'Sense of Place' },
    { value: 'race', label: 'Race & Racism' },
    { value: 'tradition', label: 'Tradition vs. Progress' },
    { value: 'other', label: 'Other' }
  ],
  
  'space-opera': [
    { value: '', label: 'Select theme' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'destiny', label: 'Destiny & Fate' },
    { value: 'exploration', label: 'Exploration' },
    { value: 'good-vs-evil', label: 'Good vs. Evil' },
    { value: 'honor', label: 'Honor & Integrity' },
    { value: 'power', label: 'Power' },
    { value: 'sacrifice', label: 'Sacrifice' },
    { value: 'survival', label: 'Survival' },
    { value: 'war', label: 'War & Conflict' },
    { value: 'other', label: 'Other' }
  ],
  
  'speculative': [
    { value: '', label: 'Select theme' },
    { value: 'ethics', label: 'Ethics' },
    { value: 'freedom', label: 'Freedom' },
    { value: 'identity', label: 'Identity' },
    { value: 'morality', label: 'Morality' },
    { value: 'progress', label: 'Progress' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'technology', label: 'Technology' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'steampunk': [
    { value: '', label: 'Select theme' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'class', label: 'Class & Social Mobility' },
    { value: 'discovery', label: 'Discovery' },
    { value: 'innovation', label: 'Innovation' },
    { value: 'progress', label: 'Progress' },
    { value: 'rebellion', label: 'Rebellion & Resistance' },
    { value: 'technology', label: 'Technology' },
    { value: 'other', label: 'Other' }
  ],
  
  'suspense': [
    { value: '', label: 'Select theme' },
    { value: 'betrayal', label: 'Betrayal' },
    { value: 'fear', label: 'Fear' },
    { value: 'justice', label: 'Justice' },
    { value: 'obsession', label: 'Obsession' },
    { value: 'power', label: 'Power' },
    { value: 'secrets', label: 'Secrets' },
    { value: 'survival', label: 'Survival' },
    { value: 'trust', label: 'Trust' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'other', label: 'Other' }
  ],
  
  'techno-thriller': [
    { value: '', label: 'Select theme' },
    { value: 'automation', label: 'Automation & AI' },
    { value: 'betrayal', label: 'Betrayal' },
    { value: 'corruption', label: 'Corruption' },
    { value: 'ethics', label: 'Ethics' },
    { value: 'human-machine', label: 'Human-Machine Relationship' },
    { value: 'power', label: 'Power' },
    { value: 'privacy', label: 'Privacy & Surveillance' },
    { value: 'survival', label: 'Survival' },
    { value: 'technology', label: 'Technology' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'other', label: 'Other' }
  ],
  
  'thriller': [
    { value: '', label: 'Select theme' },
    { value: 'betrayal', label: 'Betrayal' },
    { value: 'conspiracy', label: 'Conspiracy' },
    { value: 'corruption', label: 'Corruption' },
    { value: 'courage', label: 'Courage' },
    { value: 'fear', label: 'Fear' },
    { value: 'justice', label: 'Justice' },
    { value: 'power', label: 'Power' },
    { value: 'revenge', label: 'Revenge' },
    { value: 'survival', label: 'Survival' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'other', label: 'Other' }
  ],
  
  'urban-fantasy': [
    { value: '', label: 'Select theme' },
    { value: 'belonging', label: 'Belonging' },
    { value: 'good-vs-evil', label: 'Good vs. Evil' },
    { value: 'identity', label: 'Identity' },
    { value: 'love', label: 'Love' },
    { value: 'power', label: 'Power' },
    { value: 'redemption', label: 'Redemption' },
    { value: 'secrets', label: 'Secrets' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'war-fiction': [
    { value: '', label: 'Select theme' },
    { value: 'brotherhood', label: 'Brotherhood' },
    { value: 'courage', label: 'Courage' },
    { value: 'death', label: 'Death & Mortality' },
    { value: 'honor', label: 'Honor & Integrity' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'loyalty', label: 'Loyalty' },
    { value: 'morality', label: 'Morality' },
    { value: 'sacrifice', label: 'Sacrifice' },
    { value: 'survival', label: 'Survival' },
    { value: 'trauma', label: 'Trauma' },
    { value: 'war', label: 'War & Conflict' },
    { value: 'other', label: 'Other' }
  ],
  
  'western': [
    { value: '', label: 'Select theme' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'courage', label: 'Courage' },
    { value: 'freedom', label: 'Freedom' },
    { value: 'honor', label: 'Honor & Integrity' },
    { value: 'justice', label: 'Justice' },
    { value: 'redemption', label: 'Redemption' },
    { value: 'revenge', label: 'Revenge' },
    { value: 'survival', label: 'Survival' },
    { value: 'other', label: 'Other' }
  ],
  
  'womens-fiction': [
    { value: '', label: 'Select theme' },
    { value: 'coming-of-age', label: 'Coming of Age' },
    { value: 'empowerment', label: 'Empowerment' },
    { value: 'family', label: 'Family' },
    { value: 'friendship', label: 'Friendship' },
    { value: 'identity', label: 'Identity' },
    { value: 'love', label: 'Love' },
    { value: 'resilience', label: 'Resilience' },
    { value: 'self-discovery', label: 'Self-Discovery' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'young-adult': [
    { value: '', label: 'Select theme' },
    { value: 'belonging', label: 'Belonging' },
    { value: 'coming-of-age', label: 'Coming of Age' },
    { value: 'courage', label: 'Courage' },
    { value: 'family', label: 'Family' },
    { value: 'friendship', label: 'Friendship' },
    { value: 'identity', label: 'Identity' },
    { value: 'love', label: 'Love' },
    { value: 'purpose', label: 'Purpose & Meaning' },
    { value: 'self-discovery', label: 'Self-Discovery' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'other-fiction': ALL_AVAILABLE_THEMES,
  
  // ========================================================================
  // NON-FICTION GENRES - Theme Mappings
  // ========================================================================
  
  // Biography & Memoir
  'autobiography': [
    { value: '', label: 'Select theme' },
    { value: 'adversity', label: 'Adversity & Hardship' },
    { value: 'ambition', label: 'Ambition' },
    { value: 'coming-of-age', label: 'Coming of Age' },
    { value: 'courage', label: 'Courage' },
    { value: 'family', label: 'Family' },
    { value: 'identity', label: 'Identity' },
    { value: 'legacy', label: 'Legacy' },
    { value: 'perseverance', label: 'Perseverance' },
    { value: 'redemption', label: 'Redemption' },
    { value: 'resilience', label: 'Resilience' },
    { value: 'self-discovery', label: 'Self-Discovery' },
    { value: 'success', label: 'Success' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'biography': [
    { value: '', label: 'Select theme' },
    { value: 'achievement', label: 'Achievement' },
    { value: 'adversity', label: 'Adversity & Hardship' },
    { value: 'ambition', label: 'Ambition' },
    { value: 'courage', label: 'Courage' },
    { value: 'family', label: 'Family' },
    { value: 'identity', label: 'Identity' },
    { value: 'influence', label: 'Influence' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'legacy', label: 'Legacy' },
    { value: 'perseverance', label: 'Perseverance' },
    { value: 'success', label: 'Success' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'memoir': [
    { value: '', label: 'Select theme' },
    { value: 'adversity', label: 'Adversity & Hardship' },
    { value: 'coming-of-age', label: 'Coming of Age' },
    { value: 'family', label: 'Family' },
    { value: 'grief', label: 'Grief & Loss' },
    { value: 'identity', label: 'Identity' },
    { value: 'perseverance', label: 'Perseverance' },
    { value: 'redemption', label: 'Redemption' },
    { value: 'resilience', label: 'Resilience' },
    { value: 'self-discovery', label: 'Self-Discovery' },
    { value: 'survival', label: 'Survival' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  // Business & Professional
  'business-entrepreneurship': [
    { value: '', label: 'Select theme' },
    { value: 'achievement', label: 'Achievement' },
    { value: 'ambition', label: 'Ambition' },
    { value: 'disruption', label: 'Disruption' },
    { value: 'entrepreneurship', label: 'Entrepreneurship' },
    { value: 'hustle', label: 'Hustle & Work Ethic' },
    { value: 'innovation', label: 'Innovation' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'perseverance', label: 'Perseverance' },
    { value: 'resilience', label: 'Resilience' },
    { value: 'risk', label: 'Risk & Reward' },
    { value: 'success', label: 'Success' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'vision', label: 'Vision' },
    { value: 'other', label: 'Other' }
  ],
  
  'business-finance': [
    { value: '', label: 'Select theme' },
    { value: 'economics', label: 'Economics' },
    { value: 'growth', label: 'Personal Growth' },
    { value: 'independence', label: 'Independence' },
    { value: 'risk', label: 'Risk & Reward' },
    { value: 'security', label: 'Security' },
    { value: 'strategy', label: 'Strategy' },
    { value: 'success', label: 'Success' },
    { value: 'wealth', label: 'Wealth & Prosperity' },
    { value: 'other', label: 'Other' }
  ],
  
  'business-leadership': [
    { value: '', label: 'Select theme' },
    { value: 'authority', label: 'Authority' },
    { value: 'communication', label: 'Communication' },
    { value: 'empowerment', label: 'Empowerment' },
    { value: 'excellence', label: 'Excellence & Mastery' },
    { value: 'influence', label: 'Influence' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'mentorship', label: 'Mentorship' },
    { value: 'power', label: 'Power' },
    { value: 'responsibility', label: 'Responsibility' },
    { value: 'service', label: 'Service & Giving Back' },
    { value: 'strategy', label: 'Strategy' },
    { value: 'teamwork', label: 'Teamwork' },
    { value: 'vision', label: 'Vision' },
    { value: 'other', label: 'Other' }
  ],
  
  'business-marketing': [
    { value: '', label: 'Select theme' },
    { value: 'communication', label: 'Communication' },
    { value: 'connection', label: 'Connection' },
    { value: 'creativity', label: 'Creativity' },
    { value: 'disruption', label: 'Disruption' },
    { value: 'growth', label: 'Personal Growth' },
    { value: 'influence', label: 'Influence' },
    { value: 'innovation', label: 'Innovation' },
    { value: 'persuasion', label: 'Persuasion' },
    { value: 'strategy', label: 'Strategy' },
    { value: 'success', label: 'Success' },
    { value: 'other', label: 'Other' }
  ],
  
  'business-operations': [
    { value: '', label: 'Select theme' },
    { value: 'efficiency', label: 'Efficiency' },
    { value: 'excellence', label: 'Excellence & Mastery' },
    { value: 'growth', label: 'Personal Growth' },
    { value: 'innovation', label: 'Innovation' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'optimization', label: 'Optimization' },
    { value: 'productivity', label: 'Productivity' },
    { value: 'strategy', label: 'Strategy' },
    { value: 'systems', label: 'Systems & Processes' },
    { value: 'other', label: 'Other' }
  ],
  
  'business-real-estate': [
    { value: '', label: 'Select theme' },
    { value: 'entrepreneurship', label: 'Entrepreneurship' },
    { value: 'independence', label: 'Independence' },
    { value: 'investment', label: 'Investment' },
    { value: 'risk', label: 'Risk & Reward' },
    { value: 'strategy', label: 'Strategy' },
    { value: 'success', label: 'Success' },
    { value: 'wealth', label: 'Wealth & Prosperity' },
    { value: 'other', label: 'Other' }
  ],
  
  'business-small-business': [
    { value: '', label: 'Select theme' },
    { value: 'community', label: 'Community' },
    { value: 'customer-service', label: 'Customer Service' },
    { value: 'entrepreneurship', label: 'Entrepreneurship' },
    { value: 'hustle', label: 'Hustle & Work Ethic' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'operations', label: 'Operations' },
    { value: 'passion', label: 'Passion' },
    { value: 'perseverance', label: 'Perseverance' },
    { value: 'resilience', label: 'Resilience' },
    { value: 'success', label: 'Success' },
    { value: 'other', label: 'Other' }
  ],
  
  'career-development': [
    { value: '', label: 'Select theme' },
    { value: 'achievement', label: 'Achievement' },
    { value: 'ambition', label: 'Ambition' },
    { value: 'communication', label: 'Communication' },
    { value: 'excellence', label: 'Excellence & Mastery' },
    { value: 'growth', label: 'Personal Growth' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'mentorship', label: 'Mentorship' },
    { value: 'networking', label: 'Networking' },
    { value: 'purpose', label: 'Purpose & Meaning' },
    { value: 'reinvention', label: 'Reinvention' },
    { value: 'success', label: 'Success' },
    { value: 'work-life-balance', label: 'Work-Life Balance' },
    { value: 'other', label: 'Other' }
  ],
  
  'economics': [
    { value: '', label: 'Select theme' },
    { value: 'class', label: 'Class & Social Mobility' },
    { value: 'globalization', label: 'Globalization' },
    { value: 'inequality', label: 'Inequality' },
    { value: 'innovation', label: 'Innovation' },
    { value: 'policy', label: 'Policy' },
    { value: 'progress', label: 'Progress' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'systems', label: 'Systems & Processes' },
    { value: 'wealth', label: 'Wealth & Prosperity' },
    { value: 'other', label: 'Other' }
  ],
  
  // Technology
  'tech-ai-ml': [
    { value: '', label: 'Select theme' },
    { value: 'ai-ethics', label: 'AI Ethics' },
    { value: 'automation', label: 'Automation & AI' },
    { value: 'change', label: 'Change & Transition' },
    { value: 'disruption', label: 'Disruption' },
    { value: 'ethics', label: 'Ethics' },
    { value: 'future-of-work', label: 'Future of Work' },
    { value: 'human-machine', label: 'Human-Machine Relationship' },
    { value: 'human-potential', label: 'Human Potential' },
    { value: 'innovation', label: 'Innovation' },
    { value: 'progress', label: 'Progress' },
    { value: 'singularity', label: 'Singularity' },
    { value: 'technology', label: 'Technology' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'transhumanism', label: 'Transhumanism' },
    { value: 'other', label: 'Other' }
  ],
  
  'tech-cybersecurity': [
    { value: '', label: 'Select theme' },
    { value: 'ethics', label: 'Ethics' },
    { value: 'privacy', label: 'Privacy & Surveillance' },
    { value: 'risk', label: 'Risk & Reward' },
    { value: 'security', label: 'Security' },
    { value: 'technology', label: 'Technology' },
    { value: 'trust', label: 'Trust' },
    { value: 'other', label: 'Other' }
  ],
  
  'tech-digital-transformation': [
    { value: '', label: 'Select theme' },
    { value: 'adaptation', label: 'Adaptation' },
    { value: 'change', label: 'Change & Transition' },
    { value: 'disruption', label: 'Disruption' },
    { value: 'innovation', label: 'Innovation' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'progress', label: 'Progress' },
    { value: 'strategy', label: 'Strategy' },
    { value: 'technology', label: 'Technology' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'tech-future-trends': [
    { value: '', label: 'Select theme' },
    { value: 'ai-ethics', label: 'AI Ethics' },
    { value: 'automation', label: 'Automation & AI' },
    { value: 'change', label: 'Change & Transition' },
    { value: 'disruption', label: 'Disruption' },
    { value: 'ethics', label: 'Ethics' },
    { value: 'future-of-work', label: 'Future of Work' },
    { value: 'human-potential', label: 'Human Potential' },
    { value: 'innovation', label: 'Innovation' },
    { value: 'progress', label: 'Progress' },
    { value: 'singularity', label: 'Singularity' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'technology', label: 'Technology' },
    { value: 'transhumanism', label: 'Transhumanism' },
    { value: 'other', label: 'Other' }
  ],
  
  'tech-general': [
    { value: '', label: 'Select theme' },
    { value: 'ai-ethics', label: 'AI Ethics' },
    { value: 'discovery', label: 'Discovery' },
    { value: 'future-of-work', label: 'Future of Work' },
    { value: 'human-potential', label: 'Human Potential' },
    { value: 'innovation', label: 'Innovation' },
    { value: 'knowledge', label: 'Knowledge & Wisdom' },
    { value: 'progress', label: 'Progress' },
    { value: 'technology', label: 'Technology' },
    { value: 'other', label: 'Other' }
  ],
  
  'tech-social-impact': [
    { value: '', label: 'Select theme' },
    { value: 'change', label: 'Change & Transition' },
    { value: 'conformity', label: 'Conformity vs. Nonconformity' },
    { value: 'consumerism', label: 'Consumerism' },
    { value: 'digital-life', label: 'Digital Life' },
    { value: 'ethics', label: 'Ethics' },
    { value: 'human-machine', label: 'Human-Machine Relationship' },
    { value: 'inequality', label: 'Inequality' },
    { value: 'privacy', label: 'Privacy & Surveillance' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'technology', label: 'Technology' },
    { value: 'other', label: 'Other' }
  ],
  
  // Education & Reference
  'education': [
    { value: '', label: 'Select theme' },
    { value: 'creativity', label: 'Creativity' },
    { value: 'curiosity', label: 'Curiosity' },
    { value: 'education', label: 'Education & Learning' },
    { value: 'empowerment', label: 'Empowerment' },
    { value: 'growth', label: 'Personal Growth' },
    { value: 'knowledge', label: 'Knowledge & Wisdom' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'mentorship', label: 'Mentorship' },
    { value: 'other', label: 'Other' }
  ],
  
  'reference': [
    { value: '', label: 'Select theme' },
    { value: 'education', label: 'Education & Learning' },
    { value: 'excellence', label: 'Excellence & Mastery' },
    { value: 'knowledge', label: 'Knowledge & Wisdom' },
    { value: 'productivity', label: 'Productivity' },
    { value: 'other', label: 'Other' }
  ],
  
  // Health & Wellness
  'health-diet-nutrition': [
    { value: '', label: 'Select theme' },
    { value: 'balance', label: 'Balance' },
    { value: 'empowerment', label: 'Empowerment' },
    { value: 'growth', label: 'Personal Growth' },
    { value: 'longevity', label: 'Longevity' },
    { value: 'self-care', label: 'Self-Care' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'wellness', label: 'Wellness' },
    { value: 'other', label: 'Other' }
  ],
  
  'health-fitness': [
    { value: '', label: 'Select theme' },
    { value: 'achievement', label: 'Achievement' },
    { value: 'discipline', label: 'Discipline' },
    { value: 'empowerment', label: 'Empowerment' },
    { value: 'excellence', label: 'Excellence & Mastery' },
    { value: 'growth', label: 'Personal Growth' },
    { value: 'perseverance', label: 'Perseverance' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'health-medical': [
    { value: '', label: 'Select theme' },
    { value: 'education', label: 'Education & Learning' },
    { value: 'empowerment', label: 'Empowerment' },
    { value: 'hope', label: 'Hope' },
    { value: 'knowledge', label: 'Knowledge & Wisdom' },
    { value: 'resilience', label: 'Resilience' },
    { value: 'survival', label: 'Survival' },
    { value: 'other', label: 'Other' }
  ],
  
  'health-mental': [
    { value: '', label: 'Select theme' },
    { value: 'anxiety', label: 'Anxiety & Stress' },
    { value: 'empowerment', label: 'Empowerment' },
    { value: 'growth', label: 'Personal Growth' },
    { value: 'happiness', label: 'Happiness & Joy' },
    { value: 'hope', label: 'Hope' },
    { value: 'mindfulness', label: 'Mindfulness' },
    { value: 'resilience', label: 'Resilience' },
    { value: 'self-acceptance', label: 'Self-Acceptance' },
    { value: 'self-care', label: 'Self-Care' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'health-wellness': [
    { value: '', label: 'Select theme' },
    { value: 'balance', label: 'Balance' },
    { value: 'empowerment', label: 'Empowerment' },
    { value: 'growth', label: 'Personal Growth' },
    { value: 'happiness', label: 'Happiness & Joy' },
    { value: 'longevity', label: 'Longevity' },
    { value: 'mindfulness', label: 'Mindfulness' },
    { value: 'self-care', label: 'Self-Care' },
    { value: 'spirituality', label: 'Spirituality' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'wellness', label: 'Wellness' },
    { value: 'other', label: 'Other' }
  ],
  
  'health-alternative': [
    { value: '', label: 'Select theme' },
    { value: 'empowerment', label: 'Empowerment' },
    { value: 'healing', label: 'Healing' },
    { value: 'mindfulness', label: 'Mindfulness' },
    { value: 'nature', label: 'Nature' },
    { value: 'spirituality', label: 'Spirituality' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'wellness', label: 'Wellness' },
    { value: 'other', label: 'Other' }
  ],
  
  // History & Politics
  'history-american': [
    { value: '', label: 'Select theme' },
    { value: 'courage', label: 'Courage' },
    { value: 'culture', label: 'Culture & Heritage' },
    { value: 'diversity', label: 'Diversity & Inclusion' },
    { value: 'equality', label: 'Equality' },
    { value: 'freedom', label: 'Freedom' },
    { value: 'justice', label: 'Justice' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'legacy', label: 'Legacy' },
    { value: 'progress', label: 'Progress' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'war', label: 'War & Conflict' },
    { value: 'other', label: 'Other' }
  ],
  
  'history-world': [
    { value: '', label: 'Select theme' },
    { value: 'civilization', label: 'Civilization' },
    { value: 'culture', label: 'Culture & Heritage' },
    { value: 'globalization', label: 'Globalization' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'power', label: 'Power' },
    { value: 'progress', label: 'Progress' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'war', label: 'War & Conflict' },
    { value: 'other', label: 'Other' }
  ],
  
  'history-military': [
    { value: '', label: 'Select theme' },
    { value: 'courage', label: 'Courage' },
    { value: 'honor', label: 'Honor & Integrity' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'sacrifice', label: 'Sacrifice' },
    { value: 'strategy', label: 'Strategy' },
    { value: 'survival', label: 'Survival' },
    { value: 'war', label: 'War & Conflict' },
    { value: 'other', label: 'Other' }
  ],
  
  'political-science': [
    { value: '', label: 'Select theme' },
    { value: 'corruption', label: 'Corruption' },
    { value: 'democracy', label: 'Democracy' },
    { value: 'equality', label: 'Equality' },
    { value: 'freedom', label: 'Freedom' },
    { value: 'globalization', label: 'Globalization' },
    { value: 'justice', label: 'Justice' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'policy', label: 'Policy' },
    { value: 'power', label: 'Power' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'other', label: 'Other' }
  ],
  
  'social-commentary': [
    { value: '', label: 'Select theme' },
    { value: 'change', label: 'Change & Transition' },
    { value: 'class', label: 'Class & Social Mobility' },
    { value: 'conformity', label: 'Conformity vs. Nonconformity' },
    { value: 'consumerism', label: 'Consumerism' },
    { value: 'corruption', label: 'Corruption' },
    { value: 'culture', label: 'Culture & Heritage' },
    { value: 'diversity', label: 'Diversity & Inclusion' },
    { value: 'equality', label: 'Equality' },
    { value: 'freedom', label: 'Freedom' },
    { value: 'inequality', label: 'Inequality' },
    { value: 'progress', label: 'Progress' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'technology', label: 'Technology' },
    { value: 'tradition', label: 'Tradition vs. Progress' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'other', label: 'Other' }
  ],
  
  // Lifestyle categories with simple theme lists
  'cookbook': [
    { value: '', label: 'Select theme' },
    { value: 'culture', label: 'Culture & Heritage' },
    { value: 'family', label: 'Family' },
    { value: 'health', label: 'Health' },
    { value: 'tradition', label: 'Tradition vs. Progress' },
    { value: 'other', label: 'Other' }
  ],
  
  'crafts-hobbies': [
    { value: '', label: 'Select theme' },
    { value: 'creativity', label: 'Creativity' },
    { value: 'excellence', label: 'Excellence & Mastery' },
    { value: 'mindfulness', label: 'Mindfulness' },
    { value: 'passion', label: 'Passion' },
    { value: 'self-expression', label: 'Self-Expression' },
    { value: 'other', label: 'Other' }
  ],
  
  'home-garden': [
    { value: '', label: 'Select theme' },
    { value: 'creativity', label: 'Creativity' },
    { value: 'family', label: 'Family' },
    { value: 'nature', label: 'Nature' },
    { value: 'self-care', label: 'Self-Care' },
    { value: 'other', label: 'Other' }
  ],
  
  'travel': [
    { value: '', label: 'Select theme' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'culture', label: 'Culture & Heritage' },
    { value: 'discovery', label: 'Discovery' },
    { value: 'exploration', label: 'Exploration' },
    { value: 'freedom', label: 'Freedom' },
    { value: 'identity', label: 'Identity' },
    { value: 'nature', label: 'Nature' },
    { value: 'place', label: 'Sense of Place' },
    { value: 'self-discovery', label: 'Self-Discovery' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'sports-recreation': [
    { value: '', label: 'Select theme' },
    { value: 'achievement', label: 'Achievement' },
    { value: 'competition', label: 'Competition' },
    { value: 'courage', label: 'Courage' },
    { value: 'discipline', label: 'Discipline' },
    { value: 'excellence', label: 'Excellence & Mastery' },
    { value: 'perseverance', label: 'Perseverance' },
    { value: 'success', label: 'Success' },
    { value: 'teamwork', label: 'Teamwork' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'pets-animals': [
    { value: '', label: 'Select theme' },
    { value: 'compassion', label: 'Compassion' },
    { value: 'connection', label: 'Connection' },
    { value: 'family', label: 'Family' },
    { value: 'love', label: 'Love' },
    { value: 'nature', label: 'Nature' },
    { value: 'responsibility', label: 'Responsibility' },
    { value: 'other', label: 'Other' }
  ],
  
  // Mind & Spirit
  'philosophy': [
    { value: '', label: 'Select theme' },
    { value: 'death', label: 'Death & Mortality' },
    { value: 'ethics', label: 'Ethics' },
    { value: 'existence', label: 'Existence' },
    { value: 'freedom', label: 'Freedom' },
    { value: 'identity', label: 'Identity' },
    { value: 'knowledge', label: 'Knowledge & Wisdom' },
    { value: 'morality', label: 'Morality' },
    { value: 'purpose', label: 'Purpose & Meaning' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'virtue', label: 'Virtue' },
    { value: 'other', label: 'Other' }
  ],
  
  'psychology': [
    { value: '', label: 'Select theme' },
    { value: 'anxiety', label: 'Anxiety & Stress' },
    { value: 'behavior', label: 'Behavior' },
    { value: 'empathy', label: 'Empathy' },
    { value: 'family', label: 'Family' },
    { value: 'fear', label: 'Fear' },
    { value: 'growth', label: 'Personal Growth' },
    { value: 'happiness', label: 'Happiness & Joy' },
    { value: 'identity', label: 'Identity' },
    { value: 'motivation', label: 'Motivation' },
    { value: 'relationships', label: 'Relationships' },
    { value: 'self-discovery', label: 'Self-Discovery' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'religion-spirituality': [
    { value: '', label: 'Select theme' },
    { value: 'faith', label: 'Faith' },
    { value: 'good-vs-evil', label: 'Good vs. Evil' },
    { value: 'hope', label: 'Hope' },
    { value: 'morality', label: 'Morality' },
    { value: 'purpose', label: 'Purpose & Meaning' },
    { value: 'redemption', label: 'Redemption' },
    { value: 'spirituality', label: 'Spirituality' },
    { value: 'transcendence', label: 'Transcendence' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'mindfulness': [
    { value: '', label: 'Select theme' },
    { value: 'balance', label: 'Balance' },
    { value: 'growth', label: 'Personal Growth' },
    { value: 'happiness', label: 'Happiness & Joy' },
    { value: 'mindfulness', label: 'Mindfulness' },
    { value: 'peace', label: 'Peace' },
    { value: 'presence', label: 'Presence' },
    { value: 'self-acceptance', label: 'Self-Acceptance' },
    { value: 'spirituality', label: 'Spirituality' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  // Personal Development (Self-Help expanded)
  'self-help-general': [
    { value: '', label: 'Select theme' },
    { value: 'empowerment', label: 'Empowerment' },
    { value: 'growth', label: 'Personal Growth' },
    { value: 'happiness', label: 'Happiness & Joy' },
    { value: 'hope', label: 'Hope' },
    { value: 'purpose', label: 'Purpose & Meaning' },
    { value: 'resilience', label: 'Resilience' },
    { value: 'self-acceptance', label: 'Self-Acceptance' },
    { value: 'self-discovery', label: 'Self-Discovery' },
    { value: 'success', label: 'Success' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'self-help-habits': [
    { value: '', label: 'Select theme' },
    { value: 'discipline', label: 'Discipline' },
    { value: 'excellence', label: 'Excellence & Mastery' },
    { value: 'growth', label: 'Personal Growth' },
    { value: 'perseverance', label: 'Perseverance' },
    { value: 'productivity', label: 'Productivity' },
    { value: 'success', label: 'Success' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'self-help-relationships': [
    { value: '', label: 'Select theme' },
    { value: 'communication', label: 'Communication' },
    { value: 'connection', label: 'Connection' },
    { value: 'empathy', label: 'Empathy' },
    { value: 'family', label: 'Family' },
    { value: 'friendship', label: 'Friendship' },
    { value: 'love', label: 'Love' },
    { value: 'trust', label: 'Trust' },
    { value: 'other', label: 'Other' }
  ],
  
  'self-help-motivation': [
    { value: '', label: 'Select theme' },
    { value: 'achievement', label: 'Achievement' },
    { value: 'ambition', label: 'Ambition' },
    { value: 'courage', label: 'Courage' },
    { value: 'empowerment', label: 'Empowerment' },
    { value: 'hustle', label: 'Hustle & Work Ethic' },
    { value: 'perseverance', label: 'Perseverance' },
    { value: 'purpose', label: 'Purpose & Meaning' },
    { value: 'resilience', label: 'Resilience' },
    { value: 'success', label: 'Success' },
    { value: 'other', label: 'Other' }
  ],
  
  'self-help-confidence': [
    { value: '', label: 'Select theme' },
    { value: 'authenticity', label: 'Authenticity' },
    { value: 'courage', label: 'Courage' },
    { value: 'empowerment', label: 'Empowerment' },
    { value: 'identity', label: 'Identity' },
    { value: 'self-acceptance', label: 'Self-Acceptance' },
    { value: 'self-discovery', label: 'Self-Discovery' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'self-help-creativity': [
    { value: '', label: 'Select theme' },
    { value: 'authenticity', label: 'Authenticity' },
    { value: 'creativity', label: 'Creativity' },
    { value: 'curiosity', label: 'Curiosity' },
    { value: 'expression', label: 'Self-Expression' },
    { value: 'imagination', label: 'Imagination' },
    { value: 'innovation', label: 'Innovation' },
    { value: 'passion', label: 'Passion' },
    { value: 'other', label: 'Other' }
  ],
  
  'personal-finance': [
    { value: '', label: 'Select theme' },
    { value: 'discipline', label: 'Discipline' },
    { value: 'empowerment', label: 'Empowerment' },
    { value: 'freedom', label: 'Freedom' },
    { value: 'growth', label: 'Personal Growth' },
    { value: 'independence', label: 'Independence' },
    { value: 'security', label: 'Security' },
    { value: 'success', label: 'Success' },
    { value: 'wealth', label: 'Wealth & Prosperity' },
    { value: 'other', label: 'Other' }
  ],
  
  'parenting-family': [
    { value: '', label: 'Select theme' },
    { value: 'balance', label: 'Balance' },
    { value: 'communication', label: 'Communication' },
    { value: 'connection', label: 'Connection' },
    { value: 'family', label: 'Family' },
    { value: 'growth', label: 'Personal Growth' },
    { value: 'love', label: 'Love' },
    { value: 'responsibility', label: 'Responsibility' },
    { value: 'values', label: 'Values' },
    { value: 'other', label: 'Other' }
  ],
  
  // Science & Nature
  'science-general': [
    { value: '', label: 'Select theme' },
    { value: 'curiosity', label: 'Curiosity' },
    { value: 'discovery', label: 'Discovery' },
    { value: 'innovation', label: 'Innovation' },
    { value: 'knowledge', label: 'Knowledge & Wisdom' },
    { value: 'progress', label: 'Progress' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'other', label: 'Other' }
  ],
  
  'science-biology': [
    { value: '', label: 'Select theme' },
    { value: 'discovery', label: 'Discovery' },
    { value: 'evolution', label: 'Evolution' },
    { value: 'knowledge', label: 'Knowledge & Wisdom' },
    { value: 'life', label: 'Life' },
    { value: 'nature', label: 'Nature' },
    { value: 'other', label: 'Other' }
  ],
  
  'science-physics': [
    { value: '', label: 'Select theme' },
    { value: 'curiosity', label: 'Curiosity' },
    { value: 'discovery', label: 'Discovery' },
    { value: 'knowledge', label: 'Knowledge & Wisdom' },
    { value: 'mystery', label: 'Mystery' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'other', label: 'Other' }
  ],
  
  'science-environment': [
    { value: '', label: 'Select theme' },
    { value: 'environment', label: 'Environment & Sustainability' },
    { value: 'ethics', label: 'Ethics' },
    { value: 'nature', label: 'Nature' },
    { value: 'responsibility', label: 'Responsibility' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'survival', label: 'Survival' },
    { value: 'other', label: 'Other' }
  ],
  
  'nature-outdoors': [
    { value: '', label: 'Select theme' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'discovery', label: 'Discovery' },
    { value: 'environment', label: 'Environment & Sustainability' },
    { value: 'exploration', label: 'Exploration' },
    { value: 'nature', label: 'Nature' },
    { value: 'place', label: 'Sense of Place' },
    { value: 'wilderness', label: 'Wilderness' },
    { value: 'other', label: 'Other' }
  ],
  
  // Arts & Entertainment
  'art-photography': [
    { value: '', label: 'Select theme' },
    { value: 'beauty', label: 'Beauty' },
    { value: 'creativity', label: 'Creativity' },
    { value: 'culture', label: 'Culture & Heritage' },
    { value: 'expression', label: 'Self-Expression' },
    { value: 'identity', label: 'Identity' },
    { value: 'other', label: 'Other' }
  ],
  
  'music': [
    { value: '', label: 'Select theme' },
    { value: 'creativity', label: 'Creativity' },
    { value: 'culture', label: 'Culture & Heritage' },
    { value: 'expression', label: 'Self-Expression' },
    { value: 'identity', label: 'Identity' },
    { value: 'passion', label: 'Passion' },
    { value: 'other', label: 'Other' }
  ],
  
  'film-media': [
    { value: '', label: 'Select theme' },
    { value: 'creativity', label: 'Creativity' },
    { value: 'culture', label: 'Culture & Heritage' },
    { value: 'influence', label: 'Influence' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'storytelling', label: 'Storytelling' },
    { value: 'other', label: 'Other' }
  ],
  
  'performing-arts': [
    { value: '', label: 'Select theme' },
    { value: 'creativity', label: 'Creativity' },
    { value: 'excellence', label: 'Excellence & Mastery' },
    { value: 'expression', label: 'Self-Expression' },
    { value: 'identity', label: 'Identity' },
    { value: 'passion', label: 'Passion' },
    { value: 'other', label: 'Other' }
  ],
  
  // Writing & Language
  'writing-publishing': [
    { value: '', label: 'Select theme' },
    { value: 'creativity', label: 'Creativity' },
    { value: 'excellence', label: 'Excellence & Mastery' },
    { value: 'expression', label: 'Self-Expression' },
    { value: 'perseverance', label: 'Perseverance' },
    { value: 'storytelling', label: 'Storytelling' },
    { value: 'other', label: 'Other' }
  ],
  
  'essays': [
    { value: '', label: 'Select theme' },
    { value: 'identity', label: 'Identity' },
    { value: 'observation', label: 'Observation' },
    { value: 'reflection', label: 'Reflection' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'other', label: 'Other' }
  ],
  
  'journalism': [
    { value: '', label: 'Select theme' },
    { value: 'corruption', label: 'Corruption' },
    { value: 'justice', label: 'Justice' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'other', label: 'Other' }
  ],
  
  'poetry-nonfiction': [
    { value: '', label: 'Select theme' },
    { value: 'death', label: 'Death & Mortality' },
    { value: 'grief', label: 'Grief & Loss' },
    { value: 'identity', label: 'Identity' },
    { value: 'love', label: 'Love' },
    { value: 'nature', label: 'Nature' },
    { value: 'spirituality', label: 'Spirituality' },
    { value: 'other', label: 'Other' }
  ],
  
  'true-crime': [
    { value: '', label: 'Select theme' },
    { value: 'betrayal', label: 'Betrayal' },
    { value: 'corruption', label: 'Corruption' },
    { value: 'justice', label: 'Justice' },
    { value: 'morality', label: 'Morality' },
    { value: 'obsession', label: 'Obsession' },
    { value: 'revenge', label: 'Revenge' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'other', label: 'Other' }
  ],
  
  'other-nonfiction': ALL_AVAILABLE_THEMES,
  
  // Legacy mappings for backwards compatibility
  'business': [
    { value: '', label: 'Select theme' },
    { value: 'achievement', label: 'Achievement' },
    { value: 'ambition', label: 'Ambition' },
    { value: 'disruption', label: 'Disruption' },
    { value: 'empowerment', label: 'Empowerment' },
    { value: 'entrepreneurship', label: 'Entrepreneurship' },
    { value: 'growth', label: 'Personal Growth' },
    { value: 'hustle', label: 'Hustle & Work Ethic' },
    { value: 'innovation', label: 'Innovation' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'perseverance', label: 'Perseverance' },
    { value: 'power', label: 'Power' },
    { value: 'strategy', label: 'Strategy' },
    { value: 'success', label: 'Success' },
    { value: 'technology', label: 'Technology' },
    { value: 'other', label: 'Other' }
  ],
  
  'self-help': [
    { value: '', label: 'Select theme' },
    { value: 'empowerment', label: 'Empowerment' },
    { value: 'growth', label: 'Personal Growth' },
    { value: 'happiness', label: 'Happiness & Joy' },
    { value: 'hope', label: 'Hope' },
    { value: 'identity', label: 'Identity' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'perseverance', label: 'Perseverance' },
    { value: 'purpose', label: 'Purpose & Meaning' },
    { value: 'resilience', label: 'Resilience' },
    { value: 'self-discovery', label: 'Self-Discovery' },
    { value: 'success', label: 'Success' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'science': [
    { value: '', label: 'Select theme' },
    { value: 'curiosity', label: 'Curiosity' },
    { value: 'discovery', label: 'Discovery' },
    { value: 'innovation', label: 'Innovation' },
    { value: 'knowledge', label: 'Knowledge & Wisdom' },
    { value: 'nature', label: 'Nature' },
    { value: 'progress', label: 'Progress' },
    { value: 'technology', label: 'Technology' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'other', label: 'Other' }
  ],
  
  'health': [
    { value: '', label: 'Select theme' },
    { value: 'balance', label: 'Balance' },
    { value: 'empowerment', label: 'Empowerment' },
    { value: 'growth', label: 'Personal Growth' },
    { value: 'hope', label: 'Hope' },
    { value: 'mindfulness', label: 'Mindfulness' },
    { value: 'nature', label: 'Nature' },
    { value: 'perseverance', label: 'Perseverance' },
    { value: 'spirituality', label: 'Spirituality' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'wellness', label: 'Wellness' },
    { value: 'other', label: 'Other' }
  ],
  
  'history': [
    { value: '', label: 'Select theme' },
    { value: 'courage', label: 'Courage' },
    { value: 'culture', label: 'Culture & Heritage' },
    { value: 'freedom', label: 'Freedom' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'legacy', label: 'Legacy' },
    { value: 'power', label: 'Power' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'war', label: 'War & Conflict' },
    { value: 'other', label: 'Other' }
  ],
  
  'political': [
    { value: '', label: 'Select theme' },
    { value: 'corruption', label: 'Corruption' },
    { value: 'freedom', label: 'Freedom' },
    { value: 'justice', label: 'Justice' },
    { value: 'leadership', label: 'Leadership' },
    { value: 'power', label: 'Power' },
    { value: 'social-issues', label: 'Social Issues' },
    { value: 'truth', label: 'Truth & Authenticity' },
    { value: 'war', label: 'War & Conflict' },
    { value: 'other', label: 'Other' }
  ],
  
  'religion': [
    { value: '', label: 'Select theme' },
    { value: 'faith', label: 'Faith' },
    { value: 'good-vs-evil', label: 'Good vs. Evil' },
    { value: 'hope', label: 'Hope' },
    { value: 'morality', label: 'Morality' },
    { value: 'redemption', label: 'Redemption' },
    { value: 'spirituality', label: 'Spirituality' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'sports': [
    { value: '', label: 'Select theme' },
    { value: 'achievement', label: 'Achievement' },
    { value: 'ambition', label: 'Ambition' },
    { value: 'courage', label: 'Courage' },
    { value: 'discipline', label: 'Discipline' },
    { value: 'excellence', label: 'Excellence & Mastery' },
    { value: 'perseverance', label: 'Perseverance' },
    { value: 'success', label: 'Success' },
    { value: 'teamwork', label: 'Teamwork' },
    { value: 'transformation', label: 'Transformation' },
    { value: 'other', label: 'Other' }
  ],
  
  'poetry': [
    { value: '', label: 'Select theme' },
    { value: 'death', label: 'Death & Mortality' },
    { value: 'grief', label: 'Grief & Loss' },
    { value: 'identity', label: 'Identity' },
    { value: 'love', label: 'Love' },
    { value: 'nature', label: 'Nature' },
    { value: 'spirituality', label: 'Spirituality' },
    { value: 'other', label: 'Other' }
  ],
  
  'other': ALL_AVAILABLE_THEMES,
  
  // ========================================================================
  // CHILDREN'S GENRES - Theme Mappings
  // ========================================================================
  
  'picture-book': [
    { value: '', label: 'Select theme' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'belonging', label: 'Belonging' },
    { value: 'courage', label: 'Courage' },
    { value: 'curiosity', label: 'Curiosity' },
    { value: 'empathy', label: 'Empathy' },
    { value: 'family', label: 'Family' },
    { value: 'friendship', label: 'Friendship' },
    { value: 'imagination', label: 'Imagination' },
    { value: 'kindness', label: 'Kindness' },
    { value: 'other', label: 'Other' }
  ],
  
  'early-reader': [
    { value: '', label: 'Select theme' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'belonging', label: 'Belonging' },
    { value: 'coming-of-age', label: 'Coming of Age' },
    { value: 'courage', label: 'Courage' },
    { value: 'curiosity', label: 'Curiosity' },
    { value: 'education', label: 'Education' },
    { value: 'empathy', label: 'Empathy' },
    { value: 'family', label: 'Family' },
    { value: 'friendship', label: 'Friendship' },
    { value: 'imagination', label: 'Imagination' },
    { value: 'kindness', label: 'Kindness' },
    { value: 'other', label: 'Other' }
  ],
  
  'middle-grade': [
    { value: '', label: 'Select theme' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'belonging', label: 'Belonging' },
    { value: 'coming-of-age', label: 'Coming of Age' },
    { value: 'courage', label: 'Courage' },
    { value: 'curiosity', label: 'Curiosity' },
    { value: 'empathy', label: 'Empathy' },
    { value: 'family', label: 'Family' },
    { value: 'friendship', label: 'Friendship' },
    { value: 'good-vs-evil', label: 'Good vs. Evil' },
    { value: 'identity', label: 'Identity' },
    { value: 'imagination', label: 'Imagination' },
    { value: 'resilience', label: 'Resilience' },
    { value: 'other', label: 'Other' }
  ],
  
  'childrens-educational': [
    { value: '', label: 'Select theme' },
    { value: 'curiosity', label: 'Curiosity' },
    { value: 'discovery', label: 'Discovery' },
    { value: 'education', label: 'Education' },
    { value: 'empathy', label: 'Empathy' },
    { value: 'knowledge', label: 'Knowledge' },
    { value: 'nature', label: 'Nature' },
    { value: 'other', label: 'Other' }
  ],
  
  'childrens-activity': [
    { value: '', label: 'Select theme' },
    { value: 'creativity', label: 'Creativity' },
    { value: 'curiosity', label: 'Curiosity' },
    { value: 'imagination', label: 'Imagination' },
    { value: 'other', label: 'Other' }
  ],
  
  'childrens-poetry': [
    { value: '', label: 'Select theme' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'family', label: 'Family' },
    { value: 'friendship', label: 'Friendship' },
    { value: 'humor', label: 'Humor' },
    { value: 'imagination', label: 'Imagination' },
    { value: 'nature', label: 'Nature' },
    { value: 'other', label: 'Other' }
  ],
  
  'childrens-adventure': [
    { value: '', label: 'Select theme' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'courage', label: 'Courage' },
    { value: 'discovery', label: 'Discovery' },
    { value: 'exploration', label: 'Exploration' },
    { value: 'friendship', label: 'Friendship' },
    { value: 'good-vs-evil', label: 'Good vs. Evil' },
    { value: 'resilience', label: 'Resilience' },
    { value: 'other', label: 'Other' }
  ],
  
  'childrens-fantasy': [
    { value: '', label: 'Select theme' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'courage', label: 'Courage' },
    { value: 'good-vs-evil', label: 'Good vs. Evil' },
    { value: 'imagination', label: 'Imagination' },
    { value: 'magic', label: 'Magic' },
    { value: 'other', label: 'Other' }
  ],
  
  'childrens-animals': [
    { value: '', label: 'Select theme' },
    { value: 'empathy', label: 'Empathy' },
    { value: 'environment', label: 'Environment' },
    { value: 'family', label: 'Family' },
    { value: 'friendship', label: 'Friendship' },
    { value: 'kindness', label: 'Kindness' },
    { value: 'nature', label: 'Nature' },
    { value: 'other', label: 'Other' }
  ],
  
  'childrens-humor': [
    { value: '', label: 'Select theme' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'family', label: 'Family' },
    { value: 'friendship', label: 'Friendship' },
    { value: 'imagination', label: 'Imagination' },
    { value: 'other', label: 'Other' }
  ],
  
  'childrens-holiday': [
    { value: '', label: 'Select theme' },
    { value: 'belonging', label: 'Belonging' },
    { value: 'family', label: 'Family' },
    { value: 'friendship', label: 'Friendship' },
    { value: 'kindness', label: 'Kindness' },
    { value: 'tradition', label: 'Tradition' },
    { value: 'other', label: 'Other' }
  ],
  
  'other-children': [
    { value: '', label: 'Select theme' },
    { value: 'adventure', label: 'Adventure' },
    { value: 'belonging', label: 'Belonging' },
    { value: 'courage', label: 'Courage' },
    { value: 'curiosity', label: 'Curiosity' },
    { value: 'empathy', label: 'Empathy' },
    { value: 'family', label: 'Family' },
    { value: 'friendship', label: 'Friendship' },
    { value: 'imagination', label: 'Imagination' },
    { value: 'kindness', label: 'Kindness' },
    { value: 'other', label: 'Other' }
  ]
};

// Helper function to get themes for a given genre
export const getThemesForGenre = (genre) => {
  return GENRE_THEMES[genre] || GENRE_THEMES[''];
};

// Get all unique themes (for filter dropdowns)
export const ALL_THEMES = ALL_AVAILABLE_THEMES;
